import { SKILLS, SkillId } from '../config';
import { PrestigeState } from '../types';

// ---- Permanent State: survives prestige, persists across sessions ----
export interface PermanentState {
  totalKills: number;
  totalBossKills: number;
  totalClicks: number;
  totalGoldEarned: number;
  totalWallDamageTaken: number;
  totalHPRepaired: number;
  highestWave: number;
  totalPlayTime: number; // Stored in milliseconds

  // Prestige system
  prestige: PrestigeState;

  // Legacy flat fields (kept for migration, replaced by prestige object)
  prestigeLevel: number;
  prestigeMult: number;
  prestigeGoldMult: number;

  // Per-type kill counters (permanent across prestiges)
  bruteKills: number;
  shamanKills: number;
  boulderKills: number;
  skillsUsed: number;
  totalClickDamage: number;
  bestSingleRunGold: number;

  // Achievement state
  achievements: Record<string, { unlocked: boolean; progress: number }>;
}

// ---- Run State: resets on prestige ----
export interface RunState {
  gold: number;
  unitCounts: Record<string, number>;
  freeUnitCounts: Record<string, number>;
  upgradeLevels: Record<string, number>;
  chainProgress: Record<string, number>;
  skills: Record<string, { owned: boolean; level: number }>;
  currentWave: number;
  wallUpgradeLevel: number;
  goldEarnedThisRun: number;
  // Finger chain state
  fingerChainMult: number;
  fingerDamageFlat: number;
  fingerClickGold: number;
  fingerAoeRadius: number;
  fingerAutoClickInterval: number;
  activeLootGoblins?: { x: number; y: number; hp: number; maxHP: number; speed: number; damage: number; goldReward: number; goblinStolenGold: number }[];
}

// ---- Save format ----
interface SaveData {
  version: number;
  permanent: PermanentState;
  run: RunState;
  timestamp: number;
}

const SAVE_KEY = 'stickdefence_save';
const SAVE_VERSION = 2; // Bumped for prestige state addition

export class GameStateManager {
  permanent: PermanentState;
  savedRun: RunState | null = null; // Loaded from localStorage, consumed on GameScene.create()
  sessionStartTime: number = Date.now();
  sessionPlayTime: number = 0; // Incremented by GameScene delta
  lastSaveTimestamp: number | null = null; // Timestamp of last save, used for offline gain

  constructor() {
    this.permanent = GameStateManager.defaultPermanent();
  }

  // ---- Defaults ----

  static defaultPrestige(): PrestigeState {
    return {
      totalLegacy: 0,
      spentLegacy: 0,
      prestigeCount: 0,
      elders: { newbeginning: 0, warrior: 0, mason: 0, merchant: 0 },
      milestones: [],
      ancestralUnitDamage: 0,
    };
  }

  static defaultPermanent(): PermanentState {
    return {
      totalKills: 0,
      totalBossKills: 0,
      totalClicks: 0,
      totalGoldEarned: 0,
      totalWallDamageTaken: 0,
      totalHPRepaired: 0,
      highestWave: 0,
      totalPlayTime: 0,
      prestige: GameStateManager.defaultPrestige(),
      // Legacy fields (kept for migration)
      prestigeLevel: 0,
      prestigeMult: 1,
      prestigeGoldMult: 1,
      achievements: {},
      // Per-type kill counters
      bruteKills: 0,
      shamanKills: 0,
      boulderKills: 0,
      skillsUsed: 0,
      totalClickDamage: 0,
      bestSingleRunGold: 0,
    };
  }

  static defaultRunState(): RunState {
    return {
      gold: 0,
      unitCounts: {
        rock_thrower: 0, spear_thrower: 0, club_swinger: 0, archer: 0,
        sling_thrower: 0, longbow_archer: 0, builder: 0, crow_trainer: 0,
        crossbow: 0, firefly_nest: 0, trebuchet: 0, beehive_thrower: 0,
        druid: 0, lightning_wizard: 0,
        poison_arrows: 0, rock_rain: 0, gold_rush: 0, lightning_strike: 0,
      },
      freeUnitCounts: {},
      upgradeLevels: {
        wall_upgrade: 0,
      },
      chainProgress: {
        finger: 0, rock_thrower: 0, spear_thrower: 0, club_swinger: 0,
        archer: 0, sling_thrower: 0, longbow_archer: 0, builder: 0,
        crow_trainer: 0, crossbow: 0, firefly_nest: 0, trebuchet: 0, beehive_thrower: 0,
        druid: 0, lightning_wizard: 0,
      },
      skills: Object.fromEntries(
        (Object.keys(SKILLS) as SkillId[]).map(id => [id, { owned: false, level: 0 }])
      ),
      currentWave: 0,
      wallUpgradeLevel: 0,
      goldEarnedThisRun: 0,
      fingerChainMult: 1,
      fingerDamageFlat: 0,
      fingerClickGold: 0,
      fingerAoeRadius: 0,
      fingerAutoClickInterval: 0,
      activeLootGoblins: [],
    };
  }

  // ---- Track highest wave ----

  updateHighestWave(wave: number): void {
    if (wave > this.permanent.highestWave) {
      this.permanent.highestWave = wave;
    }
  }

  // ---- Track Time ----

  updatePlayTime(deltaMs: number): void {
    this.sessionPlayTime += deltaMs;
    this.permanent.totalPlayTime += deltaMs;
  }

  // ---- Save / Load ----

  private isWiped = false;

  save(currentRun: RunState): void {
    if (this.isWiped) return;

    const data: SaveData = {
      version: SAVE_VERSION,
      permanent: this.permanent,
      run: currentRun,
      timestamp: Date.now(),
    };
    try {
      localStorage.setItem(SAVE_KEY, JSON.stringify(data));
    } catch {
      // localStorage full or unavailable — silently fail
    }
  }

  load(): boolean {
    try {
      const raw = localStorage.getItem(SAVE_KEY);
      if (!raw) return false;
      const data = JSON.parse(raw) as SaveData;
      if (!data) return false;

      // Restore permanent state (merge with defaults for forward-compat)
      const defaultPerm = GameStateManager.defaultPermanent();
      this.permanent = { ...defaultPerm, ...data.permanent };

      // Ensure prestige object exists (migration from v1 saves)
      if (!this.permanent.prestige || typeof this.permanent.prestige !== 'object') {
        this.permanent.prestige = GameStateManager.defaultPrestige();
        // Migrate from old flat prestige fields
        if (data.permanent.prestigeLevel > 0) {
          this.permanent.prestige.totalLegacy = data.permanent.prestigeLevel * 10;
          this.permanent.prestige.prestigeCount = data.permanent.prestigeLevel;
        }
      } else {
        // Merge with defaults for forward-compat
        this.permanent.prestige = { ...GameStateManager.defaultPrestige(), ...this.permanent.prestige };
        if (!this.permanent.prestige.elders) {
          this.permanent.prestige.elders = { newbeginning: 0, warrior: 0, mason: 0, merchant: 0 };
        }
      }

      // Migration: sage → newbeginning (prestige redesign)
      const elders = this.permanent.prestige.elders as unknown as Record<string, number>;
      if ('sage' in elders) {
        if (!('newbeginning' in elders)) {
          elders['newbeginning'] = elders['sage'] || 0;
        }
        delete elders['sage'];
      }
      // Ensure all expected elder keys exist
      for (const key of ['newbeginning', 'warrior', 'mason', 'merchant'] as const) {
        if (!(key in elders)) elders[key] = 0;
      }

      // Capture save timestamp for offline gain calculation
      this.lastSaveTimestamp = data.timestamp ?? null;

      // Store run state for GameScene to consume
      this.savedRun = { ...GameStateManager.defaultRunState(), ...data.run };
      return true;
    } catch {
      return false;
    }
  }

  hasSavedRun(): boolean {
    return this.savedRun !== null;
  }

  consumeSavedRun(): RunState | null {
    const run = this.savedRun;
    this.savedRun = null;
    return run;
  }

  clearSave(): void {
    this.isWiped = true;
    localStorage.removeItem(SAVE_KEY);
    this.savedRun = null;
    this.permanent = GameStateManager.defaultPermanent();
  }
}

// Singleton — lives across scene transitions
export const gameStateManager = new GameStateManager();
